/**
 * PromoteRaceModal — confirmar promote + flujo regeneración (v15.5-§8.3.E).
 *
 * Acción "destructiva-but-not-really": al confirmar, el backend hace swap
 * demote→promote del primary anterior y REGENERA el plan activo del user
 * con los datos del nuevo primary (síncrono, patrón §8.4).
 *
 * Política UX (post-aprobación):
 * - Tono informativo (NO destructivo). Botón confirm en lime, no rojo.
 * - Copy explícito: "esto regenerará tu plan", para que el atleta sepa
 *   que el plan activo va a sustituirse.
 * - {previous}/{next} en copy: `name` si existe, si no el label
 *   localizado de la distance (vía helper compartido `distanceLabel`).
 *
 * Errores (mismo patrón que DeleteRaceModal):
 * - plan_regeneration_failed (400): mensaje genérico claro inline, NO
 *   cierra el modal. Backend ya hizo ROLLBACK COMPLETO (verificado en
 *   test_v15_5_8_3_e_plan_regeneration_failure_rollbacks_swap). Por eso
 *   el copy puede afirmar "tu carrera principal no se ha cambiado".
 * - race_target_not_found (404): la race desapareció (otro device?).
 *   Mensaje + cerramos modal (el padre dispara refresh para sincronizar).
 * - race_target_not_owned (403) / profile_not_found (404): defensivos,
 *   no deberían ocurrir en uso normal. Mensaje genérico.
 * - Network/500: genérico inline, NO cierra.
 *
 * Idempotencia: si la response llega con `was_already_primary=true`, NO
 * lo manejamos aquí — propagamos al padre vía onPromoted(response) y el
 * padre decide el feedback (toast info silencioso, sin banner).
 *
 * Cerramos el modal en ÉXITO (cualquier was_already_primary). El feedback
 * post-acción (toast + banner CTA "Ver plan nuevo →") vive en RacesPage.
 */
import { useState } from "react";

import { promoteRace } from "@/api/races";
import { distanceLabel } from "@/components/races/distanceLabel";
import { t } from "@/i18n/es";
import {
  ApiError,
  type PromoteRaceResponse,
  type RaceTarget,
} from "@/types/api";

interface Props {
  race: RaceTarget;
  /** Primary actual (para el copy "sustituirás X por Y"). Puede ser null
   *  en el caso raro donde no hay primary (post-backfill o post-delete
   *  defectuoso). El copy se ajusta. */
  currentPrimary: RaceTarget | null;
  onClose: () => void;
  /** Llamado tras éxito (200). El padre maneja feedback (toast + banner)
   *  basado en `was_already_primary` y `plan_regenerated`. */
  onPromoted: (response: PromoteRaceResponse) => void;
  /** Llamado SI race_target_not_found (404): el padre debe refrescar la
   *  lista (la race desapareció — otro device, condición de carrera). */
  onStaleData?: () => void;
}

/** Descripción para los placeholders {previous}/{next} del copy del modal.
 *  Usa `name` si existe, fallback al label localizado de la distance.
 *  Mismo helper compartido que usa RaceCard (extraído en §8.3.E). */
function _raceDescription(race: RaceTarget): string {
  if (race.name) return race.name;
  return distanceLabel(race.distance);
}

/** Mapea ApiError.code a mensaje UX i18n.
 *  IMPORTANTE: para `plan_regeneration_failed` el copy afirma "tu carrera
 *  principal no se ha cambiado" — válido SOLO porque el backend hace
 *  rollback completo (verificado en test backend §8.3.E). Si esa premisa
 *  cambia, este copy es ENGAÑOSO y hay que actualizarlo. */
function _promoteErrorMessage(code: string): string {
  switch (code) {
    case "plan_regeneration_failed":
      return t.races.errorPromotePlanRegen;
    case "race_target_not_found":
      return t.races.errorPromoteNotFound;
    case "race_target_not_owned":
      return t.races.errorNotFoundOrOwned;
    case "profile_not_found":
      return t.races.errorPromoteProfileNotFound;
    default:
      return t.races.errorGeneric;
  }
}

export function PromoteRaceModal({
  race,
  currentPrimary,
  onClose,
  onPromoted,
  onStaleData,
}: Props) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const nextLabel = _raceDescription(race);
  const previousLabel = currentPrimary
    ? _raceDescription(currentPrimary)
    : null;

  async function onConfirmPromote() {
    setSubmitting(true);
    setError(null);
    try {
      const response = await promoteRace(race.id);
      // Cualquier 200 OK → propagamos al padre (incluye was_already_primary).
      onPromoted(response);
      onClose();
    } catch (err) {
      if (err instanceof ApiError) {
        // Log técnico del detail (si el backend lo envía) para debugging,
        // no UX. El atleta ve mensaje genérico claro.
        console.error("promoteRace failed:", err.code, err.status);
        // Caso especial: la race desapareció → cerrar + refresh.
        if (err.code === "race_target_not_found") {
          setError(_promoteErrorMessage(err.code));
          // Damos al usuario 2s para leer; el padre puede preferir cerrar
          // inmediato. Simplificamos: dejamos el mensaje y NO cerramos
          // automáticamente — el padre, al recibir onStaleData, refresca.
          if (onStaleData) onStaleData();
        } else {
          setError(_promoteErrorMessage(err.code));
        }
      } else {
        setError(t.races.errorGeneric);
      }
      setSubmitting(false);
    }
  }

  // Copy principal del body: con/sin previous primary.
  const bodyText = previousLabel
    ? t.races.promoteModalBodyWithPrevious
        .replace("{previous}", previousLabel)
        .replace("{next}", nextLabel)
    : t.races.promoteModalBodyWithoutPrevious.replace("{next}", nextLabel);

  return (
    <div
      data-testid="promote-race-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="promote-race-modal-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
    >
      <div className="w-full max-w-sm rounded-lg border border-border-subtle bg-carbon-raised p-5">
        <h2
          id="promote-race-modal-title"
          className="text-lg font-medium text-bone"
        >
          {t.races.promoteModalTitle}
        </h2>
        <p
          data-testid="promote-race-modal-body"
          className="mt-2 text-sm text-bone-muted"
        >
          {bodyText}
        </p>
        <p
          data-testid="promote-race-modal-warning"
          className="mt-2 text-sm text-bone-muted"
        >
          {t.races.promoteModalRegenerateWarning}
        </p>

        {error && (
          <p
            data-testid="promote-race-modal-error"
            role="alert"
            className="mt-3 text-sm text-danger"
          >
            {error}
          </p>
        )}

        <div className="mt-5 flex items-center justify-end gap-3">
          <button
            type="button"
            data-testid="promote-race-cancel"
            onClick={onClose}
            disabled={submitting}
            className="rounded-md border border-border-subtle px-4 py-2 text-sm text-bone-muted hover:border-border-strong hover:text-bone disabled:cursor-not-allowed disabled:opacity-50"
          >
            {t.races.promoteModalCancel}
          </button>
          <button
            type="button"
            data-testid="promote-race-confirm"
            onClick={() => void onConfirmPromote()}
            disabled={submitting}
            className="rounded-md bg-lime px-4 py-2 text-sm font-medium text-graphite transition-opacity duration-fast hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {submitting
              ? t.races.promoteModalConfirmLoading
              : t.races.promoteModalConfirm}
          </button>
        </div>
      </div>
    </div>
  );
}
